import { useMemo } from 'react';
import { Scale } from 'lucide-react';
import {
  colorsForDiameter,
  computeWeightBreakdown,
  formatSmallPct,
  formatWeight,
  type SizeRule,
} from '@features/quotations/utils/pricing';
import type { SalesOrderLineItemDto } from '../types/salesOrder.types';

// 86d3ch80u — mirrors Quotation's Weight Breakdown band (QuotationLineItemsTable):
// the delivered weight split by diameter, with Total / Small / Big stat cards, a
// stacked proportion bar and a legend. Rendered below the SO line items table in
// both draft and submitted views. Shares the formatters + categorical palette with
// Quotation via @features/quotations/utils/pricing so the two never drift.

interface Props {
  lineItems: SalesOrderLineItemDto[];
  /** 14ykpqtr22u — the size rule to split by: the current one, or the one a posted SO was posted
   *  under (resolveSalesOrderSizeRule), so a posted SO's split doesn't move when the rule changes. */
  rule: SizeRule;
}

export function SalesOrderWeightBreakdown({ lineItems, rule }: Props) {
  const wb = useMemo(() => computeWeightBreakdown(lineItems, rule), [lineItems, rule]);

  if (wb.total <= 0) return null;

  return (
    <div className="border-t bg-muted/40 px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-foreground text-background shadow-sm">
            <Scale className="h-3.5 w-3.5" />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            Weight Breakdown
          </span>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          By diameter · {wb.rows.length} {wb.rows.length === 1 ? 'size' : 'sizes'}
        </span>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="border border-border bg-card px-3 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Total
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="text-xl font-bold tabular-nums text-foreground">
              {formatWeight(wb.total)}
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {Math.round(wb.total).toLocaleString()} kg
            </span>
          </div>
        </div>
        <div className="border border-info/30 bg-info/10 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-info">
              Small
            </span>
            <span className="text-[10px] font-medium tabular-nums text-info/80">{wb.smallCaption}</span>
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="text-xl font-bold tabular-nums text-foreground">
              {formatSmallPct(wb.smallPct)}
            </span>
            <span className="text-[11px] tabular-nums text-info/80">
              {formatWeight(wb.smallWeight)}
            </span>
          </div>
        </div>
        <div className="border border-border bg-muted/60 px-3 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground">
              Big
            </span>
            <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
              {wb.bigCaption}
            </span>
          </div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="text-xl font-bold tabular-nums text-foreground">
              {formatSmallPct(wb.bigPct)}
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {formatWeight(wb.bigWeight)}
            </span>
          </div>
        </div>
      </div>

      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted ring-1 ring-border"
        role="img"
        aria-label={`Weight composition by diameter: ${wb.rows
          .map((r) => `${r.diameter}mm ${formatSmallPct(r.pct)}`)
          .join(', ')}`}
      >
        {wb.rows.map((row) => (
          <div
            key={row.diameter}
            className={colorsForDiameter(row.diameter).bar}
            style={{ width: `${row.pct * 100}%` }}
            title={`${row.diameter}mm · ${formatWeight(row.weight)} · ${formatSmallPct(row.pct)}`}
          />
        ))}
        {wb.unspecified > 0 && (
          <div
            className="bg-warning"
            style={{ width: `${(wb.unspecified / wb.total) * 100}%` }}
            title={`No size · ${formatWeight(wb.unspecified)} · ${formatSmallPct(wb.unspecified / wb.total)}`}
          />
        )}
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {wb.rows.map((row) => {
          const colors = colorsForDiameter(row.diameter);
          return (
            <div key={row.diameter} className="flex items-center gap-1.5 tabular-nums">
              <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${colors.swatch}`} />
              <span className={`font-semibold ${colors.text}`}>{row.diameter}mm</span>
              <span className="font-semibold text-foreground">{formatSmallPct(row.pct)}</span>
              <span className="text-muted-foreground">({formatWeight(row.weight)})</span>
            </div>
          );
        })}
        {wb.unspecified > 0 && (
          <div className="flex items-center gap-1.5 tabular-nums">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-warning" />
            <span className="font-semibold text-warning">No size</span>
            <span className="font-semibold text-foreground">
              {formatSmallPct(wb.unspecified / wb.total)}
            </span>
            <span className="text-muted-foreground">({formatWeight(wb.unspecified)})</span>
          </div>
        )}
      </div>
    </div>
  );
}
